import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { m } from 'framer-motion';
import { StatCard } from 'src/components/actionCards';
import { computeDelta } from 'src/utils/comparisonPresets';
import useCountUp from 'src/hooks/useCountUp';
import { accentForColor } from './reportAccents';

/**
 * Reporting KPI card — a thin adapter over the shared StatCard so Reporting pages use the
 * exact same shell as the Launchpad / table strips: header row (dot + UPPER-CASE label),
 * dark number, TrendPill for the period comparison, grey chip for conversion rate, icon
 * top-right, soft-shadow selected state. Numbers are never coloured; the accent comes from
 * the metric's meaning (`accent`, else derived from the legacy `color` name).
 */
export default function KPICard({
    label,
    value,
    previousValue = 0,
    color = 'blue',
    accent = null,
    conversionRate = null,
    prefix = '',
    suffix = '',
    formatValue = null,
    comparisonType = 'period',
    comparisonLabel = null,
    deltaPercent = undefined,
    deltaInverted = false,
    onClick = null,
    tooltipText = null,
    icon = null,
    topIcon = null,
    subText = null,
    scrollDirection = 'up',
    active = false,
    hideDelta,
    filterCard = false,
    minWidth = 150,
}) {
    const isTextValue = Boolean(icon) || typeof value === 'string';

    // Count-up/down animation for numeric values only.
    // Pass null for text/icon cards so useCountUp bails immediately without scheduling a RAF.
    // The shared count-up rounds to integers, so fractional values (currency pence, rates)
    // skip the animation and render exactly as supplied.
    const isFractional = typeof value === 'number' && !Number.isInteger(value);
    const animatedValue = useCountUp(isTextValue || isFractional ? null : (value || 0), 1200, scrollDirection || 'up');
    const shownNumber = isFractional ? value : animatedValue;

    let displayValue;
    if (isTextValue) {
        displayValue = value || '-';
    } else if (formatValue) {
        displayValue = formatValue(shownNumber);
    } else {
        displayValue = `${prefix}${shownNumber?.toLocaleString() || 0}${suffix}`;
    }

    const comparisonText = comparisonLabel
        || (comparisonType === 'yoy' ? 'vs same period last year' : 'vs previous period');

    // Shared computeDelta() — same formula/null-handling as the User Performance table.
    // null (previous 0, current >0, or no previous) renders as a grey "—" pill.
    // `deltaPercent` lets a caller supply a server-computed change instead (Group dashboard).
    let delta;
    if (hideDelta) {
        delta = undefined;
    } else if (deltaPercent !== undefined && deltaPercent !== null) {
        delta = {
            value: Number(deltaPercent),
            inverted: Boolean(deltaInverted),
            tooltip: `${Number(deltaPercent) >= 0 ? '+' : '-'}${Math.abs(Number(deltaPercent)).toFixed(1)}% vs previous period`,
        };
    } else if (!isTextValue && comparisonType) {
        const hasPrevious = previousValue !== null && previousValue !== undefined;
        const pct = hasPrevious ? computeDelta(value, previousValue) : null;
        const previousFormatted = formatValue
            ? formatValue(previousValue || 0)
            : `${prefix}${previousValue?.toLocaleString() || 0}${suffix}`;
        delta = {
            value: pct,
            tooltip: pct === null
                ? `No comparable previous value ${comparisonText}`
                : `${pct >= 0 ? '+' : '-'}${Math.abs(pct).toFixed(1)}% ${comparisonText}. Previous: ${previousFormatted}`,
        };
    }

    const chips = [];
    if (subText) chips.push({ key: 'sub', text: subText, type: 'info', showDot: false });
    if (conversionRate !== null && conversionRate !== undefined && conversionRate > 0) {
        chips.push({ key: 'conv', text: `${conversionRate}%`, type: 'info', showDot: false });
    }

    return (
        <Box
            component={m.div}
            data-testid="kpi-card"
            data-active={active ? 'true' : undefined}
            variants={{
                initial: { opacity: 0, y: 24, scale: 0.96 },
                animate: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
                },
            }}
            sx={{ flex: 1, minWidth: `${minWidth}px`, display: 'flex' }}
        >
            <StatCard
                label={label}
                value={displayValue}
                accentColor={accent || accentForColor(color)}
                icon={icon || topIcon || undefined}
                tooltip={tooltipText || undefined}
                chips={chips.length ? chips : undefined}
                delta={delta}
                onClick={onClick || undefined}
                selected={Boolean(active)}
                filterCard={filterCard}
                size={filterCard ? 'sm' : undefined}
                valueSx={isTextValue ? { fontSize: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 } : { whiteSpace: 'nowrap' }}
            />
        </Box>
    );
}

KPICard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    previousValue: PropTypes.number,
    hideDelta: PropTypes.bool,
    color: PropTypes.oneOf(['green', 'blue', 'purple', 'yellow', 'red', 'cyan']),
    // Explicit flat accent (hex) — wins over the legacy `color` name. See reportAccents.js.
    accent: PropTypes.string,
    conversionRate: PropTypes.number,
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    formatValue: PropTypes.func,
    comparisonType: PropTypes.oneOf(['period', 'yoy']),
    comparisonLabel: PropTypes.string,
    // Server-computed period change (%), shown instead of deriving from previousValue.
    deltaPercent: PropTypes.number,
    // true for cost-type metrics where DOWN is good.
    deltaInverted: PropTypes.bool,
    onClick: PropTypes.func,
    tooltipText: PropTypes.string,
    icon: PropTypes.string,
    // Legacy per-icon colours are accepted but ignored: the icon is tinted by the accent.
    iconColor: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    iconBg: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    topIcon: PropTypes.string,
    topIconColor: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    topIconBg: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    subText: PropTypes.string,
    scrollDirection: PropTypes.oneOf(['up', 'down']),
    active: PropTypes.bool,
    // The card filters a list (Call Logs): strong active state, one-line label, compact.
    filterCard: PropTypes.bool,
    // Minimum card width in px (compact strips pass less than the 150 default).
    minWidth: PropTypes.number,
};
